<script setup>
import { computed, ref } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import AppTextarea from '@/components/atoms/AppTextarea'
import { formatAttachmentSize } from '@/helpers/support'
import { SUPPORT_ATTACHMENT_MAX_FILES, SUPPORT_ATTACHMENT_TYPES } from '@/constants'

/**
 * The Chat tab's composer, in the panel's footer (Figma 28708:73192 / 29855:72883): a gray-50
 * box holding a multi-line field, the paperclip and Send. Enter sends too; Shift+Enter breaks
 * the line. Attachments have no frame: picked files stack above the box, each with its own
 * remove, and a file the service would refuse says why.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  // Pending uploads from the store: { tempId, name, size, previewUrl, status, error }
  attachments: { type: Array, default: () => [] },
  // A finished upload is a message on its own.
  ready: { type: Boolean, default: false },
  sending: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'send', 'attach', 'remove'])

const fileInput = ref(null)

// Something to send, and nothing still on its way.
const canSend = computed(() => (props.modelValue.trim() !== '' || props.ready) && !props.sending && !props.uploading)

const send = () => {
  if (canSend.value) emit('send')
}

const pick = () => fileInput.value?.click()

// The same input serves every pick, so it is cleared — picking the same file twice in a row
// fires no `change` otherwise.
const onPicked = (event) => {
  const files = event.target.files
  if (files?.length) emit('attach', files)
  event.target.value = ''
}
</script>

<template>
  <div class="flex flex-col gap-y-2">
    <!-- What is about to be sent, newest last; each chip can be taken back off. -->
    <ul v-if="attachments.length" class="flex flex-col gap-y-1.5">
      <li
        v-for="file in attachments"
        :key="file.tempId"
        class="flex items-center gap-x-2 rounded-lg border px-2 py-1.5"
        :class="file.status === 'error' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'"
      >
        <img v-if="file.previewUrl" :src="file.previewUrl" alt="" class="size-7 shrink-0 rounded object-cover" />
        <span v-else class="flex size-7 shrink-0 items-center justify-center rounded bg-white">
          <Icon icon="heroicons-outline:document" class="size-4 text-gray-400" />
        </span>

        <span class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-xs font-medium text-gray-700" v-text="file.name" />
          <span
            class="truncate text-[11px]"
            :class="file.status === 'error' ? 'text-red-600' : 'text-gray-500'"
            v-text="file.error || (file.status === 'uploading' ? 'Uploading…' : formatAttachmentSize(file.size))"
          />
        </span>

        <AppButton
          variant="link"
          :coloured="false"
          class="!h-auto !p-1"
          :aria-label="`Remove ${file.name}`"
          @click="emit('remove', file.tempId)"
        >
          <Icon icon="heroicons-solid:x" class="size-3.5 shrink-0 text-gray-400" />
        </AppButton>
      </li>
    </ul>

    <!-- Figma: a 12px inset, 8px between the text and the buttons, gray-50 under a gray-200 stroke with a 2px shadow. -->
    <div class="flex flex-col gap-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-sm transition-colors focus-within:border-tint-1">
      <AppTextarea
        :model-value="modelValue"
        variant="flush"
        size="md"
        :rows="2"
        :max-rows="6"
        submit-on-enter
        placeholder="Ask me anything"
        aria-label="Message support"
        :disabled="sending"
        @update:model-value="emit('update:modelValue', $event)"
        @submit="send"
      />

      <div class="flex items-center justify-between gap-x-3">
        <!-- Figma's 40 × 34 outlined button around the 16px clip. -->
        <AppButton
          variant="outlined"
          rounded="lg"
          class="!w-10 !px-0"
          :disabled="sending || attachments.length >= SUPPORT_ATTACHMENT_MAX_FILES"
          aria-label="Attach a file"
          @click="pick"
        >
          <Icon icon="heroicons-outline:paper-clip" class="size-4 shrink-0 text-gray-700" />
        </AppButton>

        <AppButton variant="default" rounded="lg" :disabled="!canSend" :loading="sending" @click="send">
          Send
        </AppButton>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      multiple
      class="hidden"
      :accept="SUPPORT_ATTACHMENT_TYPES.join(',')"
      @change="onPicked"
    />

    <!-- Sending now would drop the file, so say so rather than disable with no reason. -->
    <p v-if="uploading" class="text-[11px] text-gray-500">Waiting for the upload to finish…</p>
  </div>
</template>
